import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {Badge} from '@/components/ui/badge'
import {Pagination} from '@/components/ui/pagination'
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table'
import {ErrorBanner} from '@/components/ui/error-banner'
import {usePageTitle} from '@/hooks/use-page-title'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {routes} from '@/lib/routes'
import {formatTimestamp} from '@/lib/date-utils'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {MutedText} from '@/components/shared/MutedText'
import {TruncatedCell} from '@/components/shared/TruncatedCell'
import {PageLayout} from '@/components/shared/PageLayout'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {useIsEmbedded} from '@/components/shared/EmbeddedContext'
import {NotificationTemplateControllerListEndpoint} from '@/types/endpoint-contracts'
import {CONCEPT_ICON} from '@/lib/concepts'

/**
 * Strip Thymeleaf `<span th:text="..." th:remove="tag">Fallback</span>` wrappers
 * down to their fallback text. The fallback is what the template engine itself
 * would emit when an interpolation has no binding — it's a usable preview for
 * the admin index, where the actual binding doesn't exist (the templates are
 * being browsed, not fired).
 */
export function stripThymeleafTags(source: string | null | undefined): string {
    if (!source) return ''
    return source.replace(/<span\b[^>]*>([^<]*)<\/span>/g, '$1')
}

export function NotificationTemplates() {
    const i18n = useI18n()
    const services = useServices()
    const embedded = useIsEmbedded()
    usePageTitle(embedded ? undefined : i18n.notificationTemplates.pageTitle)

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: NotificationTemplateControllerListEndpoint,
        sortable: false,
        defaultSortBy: '',
        defaultSortDir: SortDir.ASC,
    })

    const {data: templatesPage, isLoading, error} = useQuery({
        queryKey: ['notification-templates', queryString, page, pageSize],
        queryFn: () => services.notificationTemplates.list(
            Object.fromEntries(new URLSearchParams(queryString))),
    })

    const templates = templatesPage?.content ?? []
    const totalItems = templatesPage?.totalElements ?? 0
    const totalPages = templatesPage?.totalPages ?? 0

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    if (error) {
        return <ErrorBanner error={error}/>
    }

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.notificationTemplate}
            title={i18n.notificationTemplates.title}
            count={totalItems}
            data-testid="notification-templates-list"
            filters={
                <UnifiedSearchBar
                    contract={NotificationTemplateControllerListEndpoint}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    placeholder={i18n.notificationTemplates.searchPlaceholder}
                />
            }
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            {isLoading && (
                <MutedText>{i18n.common.loading}</MutedText>
            )}
            <Table data-testid="notification-templates-table" data-help-prefix="notificationTemplates">
                <TableHeader>
                    <TableRow data-readonly>
                        <TableHead data-testid="col-key" data-help-key="key">{i18n.notificationTemplates.columns.key}</TableHead>
                        <TableHead data-testid="col-subject" data-help-key="subject">{i18n.notificationTemplates.columns.subject}</TableHead>
                        <TableHead data-testid="col-state" data-help-key="state">{i18n.notificationTemplates.columns.state}</TableHead>
                        <TableHead data-testid="col-active" data-help-key="active">{i18n.notificationTemplates.columns.active}</TableHead>
                        <TableHead data-testid="col-last-modified" data-help-key="lastModified">{i18n.notificationTemplates.columns.lastModified}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {templates.map((template) => (
                        <ClickableTableRow
                            key={template.templateKey}
                            to={routes.admin.notificationTemplates.detail(template.templateKey)}
                            data-testid={`notification-template-row-${template.templateKey}`}
                        >
                            <TableCell>
                                <Badge variant="outline" className="font-mono">{template.templateKey}</Badge>
                            </TableCell>
                            <TruncatedCell className="text-muted-foreground" data-testid={`template-subject-${template.templateKey}`}>
                                {stripThymeleafTags(template.subject)}
                            </TruncatedCell>
                            <TableCell>
                                {template.customised ? (
                                    <Badge variant="secondary" data-testid="badge-customised">
                                        {i18n.notificationTemplates.badges.customised}
                                    </Badge>
                                ) : (
                                    <Badge variant="outline" data-testid="badge-default">
                                        {i18n.notificationTemplates.badges.default}
                                    </Badge>
                                )}
                            </TableCell>
                            <TableCell>
                                <Badge variant={template.active ? 'success' : 'warning'}>
                                    {template.active
                                        ? i18n.notificationTemplates.badges.active
                                        : i18n.notificationTemplates.badges.inactive}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <MutedText>{formatTimestamp(template.updatedAt)}</MutedText>
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {!isLoading && templates.length === 0 && (
                        <TableEmptyState
                            colSpan={5}
                            hasFilters={hasActiveFilters}
                            matchMessage={i18n.notificationTemplates.noMatching}
                            emptyMessage={i18n.notificationTemplates.emptyState}
                        />
                    )}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
